import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Receipt, Briefcase, Inbox, Plus, Clock, Wallet, Ruler, CalendarDays, ArrowRight, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { TrackingLine } from '@/components/QuoteTracking';
import DashboardCharts from '@/components/DashboardCharts';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

function Kpi({ to, Icon, label, value, sub, tone, tid }) {
  return (
    <Link to={to} data-testid={tid} className="tm-card tm-card-interactive p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="w-9 h-9 rounded-lg bg-brand/10 text-brand grid place-items-center"><Icon size={17} /></span>
        <ArrowRight size={15} className="text-slate-300" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</p>
        <p className="font-display text-3xl font-semibold text-slate-900 mt-1" data-testid={`${tid}-value`}>{value}</p>
        <p className={`text-xs mt-1 ${tone === 'warn' ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>{sub}</p>
      </div>
    </Link>
  );
}

const ACTIONS = [
  { to: '/estimator', label: 'Create quote', Icon: FileText, primary: true, tid: 'ws-qa-quote' },
  { to: '/invoices', label: 'Create invoice', Icon: Receipt, tid: 'ws-qa-invoice' },
  { to: '/jobs', label: 'New job', Icon: Briefcase, tid: 'ws-qa-job' },
  { to: '/timesheets', label: 'Log hours', Icon: Clock, tid: 'ws-qa-hours' },
  { to: '/receipts', label: 'Add receipt', Icon: Wallet, tid: 'ws-qa-receipt' },
  { to: '/plan-designer', label: 'Design a plan', Icon: Ruler, tid: 'ws-qa-plan' },
];

function Panel({ title, to, linkLabel, children, tid }) {
  return (
    <section className="tm-card p-5" data-testid={tid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        {to && <Link to={to} className="text-xs font-semibold text-brand hover:underline">{linkLabel}</Link>}
      </div>
      {children}
    </section>
  );
}

const Empty = ({ children }) => <p className="text-sm text-slate-500 py-4">{children}</p>;

export default function WorkspacePage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/workspace/overview').then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  const q = d?.quotes, inv = d?.invoices;

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="workspace-page">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">My workspace</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="ws-greeting">
        {greeting()}, {user?.name?.split(' ')[0] || 'there'}
      </h1>
      <p className="text-slate-500 mt-1">{d?.business_name || user?.business_name || 'Your business'} at a glance.</p>

      {d === false && <p className="mt-6 text-rose-600 text-sm">Could not load your dashboard — please refresh.</p>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Kpi tid="ws-kpi-quotes" to="/dashboard" Icon={FileText} label="Quotes awaiting" value={q ? q.awaiting : '–'}
          sub={q ? `${gbp(q.awaiting_value)} pending${q.hot ? ` · ${q.hot} hot` : ''}` : 'Loading…'} />
        <Kpi tid="ws-kpi-invoices" to="/invoices" Icon={Receipt} label="Unpaid invoices" value={inv ? gbp(inv.outstanding_value) : '–'}
          sub={inv ? (inv.overdue ? `${inv.overdue} overdue` : `${inv.outstanding} outstanding`) : 'Loading…'} tone={inv?.overdue ? 'warn' : ''} />
        <Kpi tid="ws-kpi-jobs" to="/jobs" Icon={Briefcase} label="Active jobs" value={d ? d.jobs.active : '–'} sub={q ? `${q.accepted_month} won this month · ${gbp(q.accepted_month_value)}` : 'Loading…'} />
        <Kpi tid="ws-kpi-enquiries" to="/business?tab=enquiries" Icon={Inbox} label="Enquiries this month" value={d ? d.enquiries.count : '–'} sub={d ? `${d.enquiries.won} won` : 'Loading…'} />
      </div>

      <section className="mt-8" data-testid="ws-quick-actions">
        <h2 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Quick actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ACTIONS.map((a) => (
            <Link key={a.to} to={a.to} data-testid={a.tid}
              className={`rounded-xl border px-4 py-4 flex flex-col items-start gap-2 text-sm font-semibold transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lg ${
                a.primary ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-800'}`}>
              <span className={`w-8 h-8 rounded-lg grid place-items-center ${a.primary ? 'bg-brand text-brand-ink' : 'bg-slate-100 text-slate-700'}`}>
                {a.primary ? <Plus size={16} /> : <a.Icon size={16} />}
              </span>
              {a.label}
            </Link>
          ))}
        </div>
      </section>

      <DashboardCharts charts={d?.charts} />

      <div className="grid lg:grid-cols-5 gap-6 mt-8">
        <div className="lg:col-span-3 space-y-6">
          <Panel tid="ws-recent-quotes" title="Recent quotes" to="/dashboard" linkLabel="All quotes">
            {!q?.recent?.length ? <Empty>No quotes yet — create your first one above.</Empty> : (
              <ul className="divide-y divide-slate-100">
                {q.recent.map((p) => (
                  <li key={p.id}>
                    <Link to={`/estimator/${p.id}`} className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg" data-testid={`ws-recent-quote-${p.id}`}>
                      <span className="min-w-0">
                        <span className="block font-semibold text-slate-900 truncate">{p.name?.trim() || 'Untitled quote'}</span>
                        <span className="block text-xs text-slate-500">{p.customer_name || 'No customer'} · <span className="capitalize">{p.status}</span></span>
                        <TrackingLine p={p} />
                      </span>
                      <span className="font-display text-lg text-slate-900 shrink-0">{gbp(p.total)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
        <div className="lg:col-span-2 space-y-6">
          <Panel tid="ws-outstanding-invoices" title="Money owed" to="/invoices" linkLabel="Invoices">
            {!inv?.list?.length ? <Empty>Nothing outstanding. Nice.</Empty> : (
              <ul className="divide-y divide-slate-100">
                {inv.list.map((i) => (
                  <li key={i.id}>
                    <Link to={`/invoices/${i.id}`} className="flex items-center justify-between gap-2 py-2.5 text-sm" data-testid={`ws-invoice-${i.id}`}>
                      <span className="min-w-0">
                        <span className="font-semibold">{i.number || 'Invoice'}</span> <span className="text-slate-500">{i.customer_name}</span>
                        {i.overdue && <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600"><AlertTriangle size={11} /> Overdue</span>}
                      </span>
                      <span className="font-semibold shrink-0">{gbp(i.balance_due)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel tid="ws-upcoming" title="Coming up" to="/business?tab=calendar" linkLabel="Calendar">
            {!d?.events?.length && !d?.jobs?.list?.length ? <Empty>Nothing booked in the next two weeks.</Empty> : (
              <ul className="space-y-2.5 text-sm">
                {d.events.map((e) => (
                  <li key={e.id} className="flex items-start gap-3" data-testid={`ws-event-${e.id}`}>
                    <CalendarDays size={15} className="mt-0.5 text-brand shrink-0" />
                    <span><span className="font-semibold">{e.title}</span><span className="block text-xs text-slate-500">{new Date(e.start).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{e.location ? ` · ${e.location}` : ''}</span></span>
                  </li>
                ))}
                {d.jobs.list.map((j) => (
                  <li key={j.id}>
                    <Link to={`/jobs/${j.id}`} className="flex items-start gap-3" data-testid={`ws-job-${j.id}`}>
                      <Briefcase size={15} className="mt-0.5 text-slate-400 shrink-0" />
                      <span><span className="font-semibold">{j.name}</span><span className="block text-xs text-slate-500">In progress{j.customer_name ? ` · ${j.customer_name}` : ''}</span></span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
